import React, { useState } from "react";
import { conversationView, conversationAction, subjectLine, opening } from "./lead/leadModel.mjs";
import { toDate } from "./format.mjs";

/*
 * LATEST CONVERSATION (frames 1a, 1d, 1i): who wrote last and when, the subject on
 * the sub-line with the thread's company-wide count and range, their message at
 * reading size with its line breaks, and what has happened since ("Since then ·
 * You: Hey Shahar, welcome to Remotion… · Oct 1 · 09:42"). Every word of it is
 * leadModel.conversationView's, which the tests pin.
 *
 *   - WHO is the person in full ("Ella Moss"), with their title when they are not
 *     the person whose record this is, and the time in THEIR zone beside mine
 *     ("Sep 30 · 10:40 ET · 17:40 your time"; once when the clocks agree, Pluto).
 *     A message held by two people with no intro reads "inbound": nobody can say.
 *   - THE COLLEAGUE BEFORE THEM on the same thread, when it was someone else at the
 *     company, as a one-line quote above (1d: "Priya, Sep 30: Looping in Ella for
 *     legal."). The "to you, Cc Priya" of the frame is not drawn: an inbound
 *     message's To and Cc are not stored (reality.txt [8]).
 *   - A REPLY OWED makes this the lead block (the shell orders it first): "Latest
 *     conversation · reply owed". No dot here: the pulsing red dot is Next's, on
 *     the Reply line, and one is enough (frame 1d draws none on this card). The
 *     composer docks INSIDE this card, under the message, when the action answers
 *     it (`children`, the shell's docking; frame 1d).
 *   - AFTER A SEND FROM THIS PAGE the message tops the conversation (1i: "You → Ella,
 *     Cc Priya · just now", "Before it · Ella: Two redlines…"), while the shell
 *     still holds the receipt.
 *   - "snippet only" on mail the watcher kept as a snippet (before Aug 2026), and
 *     "Open in Gmail ↗" for the thread.
 *   - READ-ONLY, one line, where the matrix says Summary (churned, lost).
 *   - A WHITE Reply or Nudge only when Next has no composer line for whoever wrote
 *     last (leadModel.conversationAction): Next owns the action, and a second
 *     button for the same email is the old card's mistake; but a conversation
 *     Next never asks about (no company, a client's question, a line hidden with
 *     its ×) still has to be answerable from the record.
 *
 * Props:
 *   record   the shell's bundle: timeline.rows, company, contact, zones, next,
 *            sent (every send from this page, owed ones included), now
 *   ops      ops.compose, for the white Reply / Nudge
 *   state    "open" | "summary": the matrix cell; summary is the read-only line
 *   owed     boolean: a reply is owed, and the label says so
 *   children the docked composer, drawn inside the card under the message
 */
const JUST_SENT_MS = 30 * 60000;

export default function LatestConversation({ record, ops = null, state = "open", owed = false, children = null }) {
  const now = record.now || new Date();
  const [actError, setActError] = useState(null);
  // record.sent, not next.receipts: Next leaves an owed send out of its receipts
  // (the line stays, reworded), but the email still went and tops the thread.
  const receipts = record.sent || (record.next && record.next.receipts) || [];
  const justSent = receipts.some((r) => toDate(r.at) && toDate(now).getTime() - toDate(r.at).getTime() < JUST_SENT_MS);
  const v = conversationView(record.timeline.rows, { company: record.company, contact: record.contact, zones: record.zones, justSent, now });
  // No conversation yet: the composer still has to land somewhere.
  if (!v) return children ? <>{children}</> : null;
  const { lead } = v;
  const gmail = v.thread.url && (
    <a className="rec-convo__gmail" href={v.thread.url} target="_blank" rel="noopener noreferrer">
      Open in Gmail ↗
    </a>
  );
  const sub = subjectLine(v);
  // Not while a composer is docked in here: that is this button's answer already.
  const act = !children && ops ? conversationAction(v, { next: record.next, company: record.company, state, now }) : null;
  const actEl = act && (
    <div className="rec-inline rec-convo__acts">
      <button
        type="button"
        className="rec-btn rec-btn--secondary rec-btn--sm"
        onClick={() => {
          setActError(null);
          try {
            ops.compose({ kind: act.kind, action_kind: act.kind, verb: act.label, contact_id: act.contact_id, age_days: act.age_days, line_key: `convo:${act.kind}:${act.contact_id}` });
          } catch (e) {
            setActError(e.message);
          }
        }}
      >
        {act.label}
      </button>
      {actError && <span className="rec-err">{actError}</span>}
    </div>
  );

  if (v.mode === "sent") {
    return (
      <section className="rec-card rec-convo rec-convo--sent" aria-label="Latest conversation">
        <div className="rec-card__head">
          <span className="rec-label">Latest conversation</span>
          {gmail}
        </div>
        <div className="rec-convo__who">
          <b>{lead.who}</b> <span className="rec-convo__when">· {lead.when}</span>
        </div>
        {lead.body && <div className="rec-convo__line">{lead.body}</div>}
        {v.before && (
          <div className="rec-convo__before">
            Before it · <b>{v.before.who}:</b> {v.before.text}
          </div>
        )}
        {children}
      </section>
    );
  }

  if (state === "summary") {
    return (
      <section className="rec-card rec-convo rec-convo--sent" aria-label="Latest conversation">
        <div className="rec-card__head">
          <span className="rec-label">Latest conversation · read-only</span>
          {gmail}
        </div>
        <div className="rec-convo__who">
          <b>{lead.who}</b> <span className="rec-convo__when">· {lead.when}</span>
        </div>
        {lead.body && <div className="rec-convo__line">{opening(lead.row.body, 160)}</div>}
        {sub && <div className="rec-convo__subject">{sub}</div>}
        {children}
      </section>
    );
  }

  return (
    <section className={`rec-card rec-convo${owed ? " rec-convo--owed" : ""}`} aria-label="Latest conversation">
      <div className="rec-card__head">
        <span className="rec-label rec-convo__label">Latest conversation{owed ? " · reply owed" : ""}</span>
        {gmail}
      </div>
      {v.quote && (
        <div className="rec-quoteline rec-convo__quote">
          <b>
            {v.quote.who}, {v.quote.day}:
          </b>{" "}
          {v.quote.text}
        </div>
      )}
      <div className="rec-convo__head">
        <div className="rec-convo__who">
          <b>{lead.who}</b>{" "}
          <span className="rec-convo__when">
            · {lead.title ? `${lead.title} · ` : ""}
            {lead.when}
          </span>
        </div>
        {sub && <div className="rec-convo__subject">{sub}</div>}
      </div>
      {lead.body && <div className="rec-convo__body">{lead.body}</div>}
      {v.after && (
        // One line, as drawn: the words give way to an ellipsis, the time stays.
        <div className="rec-convo__since">
          <span className="rec-convo__sincetext">
            Since then · <b>{v.after.who}:</b> {v.after.body}
          </span>
          <span className="rec-meta rec-convo__sincewhen">· {v.after.when}</span>
        </div>
      )}
      {actEl}
      {children}
    </section>
  );
}
